import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import Chart from "react-apexcharts";
import {
  Users,
  CheckCircle,
  Video,
  Cpu,
  AlertCircle,
  DollarSign,
  ExternalLink,
  AlertTriangle,
  RefreshCw,
  Zap,
} from "react-feather";
import {
  platformDashboardAPI,
  getMeetingsAPI,
  getTenantsAPI,
  getSubscriptionsAPI,
  getIntegrationsMonitorAPI,
  getJobsFailuresAPI,
} from "../../../api/api";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import { platformUrl } from "../../../utils/mhNav";
import styles from "./ExecutiveDashboard.module.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function KPI({ title, value, icon: Icon, color, to, navigate }) {
  const handleClick = to ? () => navigate(to) : undefined;
  return (
    <div
      className={`${styles.kpiCard} ${to ? styles.clickable : ""}`}
      style={{ "--kpi-color": color }}
      onClick={handleClick}
      role={to ? "button" : undefined}
    >
      <div className={styles.kpiIcon}>
        <Icon size={18} />
      </div>
      <div className={styles.kpiLabel}>{title}</div>
      <div className={styles.kpiValue}>{value ?? "—"}</div>
    </div>
  );
}

function CardShell({ title, children, action }) {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span>{title}</span>
        {action ?? <span className={styles.cardHeaderDot} />}
      </div>
      {children}
    </div>
  );
}

// ── Derive meetings-by-day buckets from raw meeting array ─────────────────────
function buildMeetingsByDay(meetings = []) {
  const counts = {};
  meetings.forEach((m) => {
    const day = (m.startAt || m.createdAt || "").slice(0, 10);
    if (day) counts[day] = (counts[day] || 0) + 1;
  });
  return Object.entries(counts)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-30) // last 30 days max
    .map(([date, count]) => ({ date, count }));
}

// ── Derive connector-strip shape from integrations-monitor response ───────────
function buildConnectorStrip(integrations = []) {
  return integrations.map((i) => ({
    id: i._id ?? i.id ?? i.provider,
    name: i.name ?? i.provider ?? i.type ?? "Unknown",
    providerKey: i.provider ?? i.providerKey,
    status:
      i.status === "active" || i.connected
        ? "healthy"
        : i.status === "degraded"
          ? "degraded"
          : "down",
  }));
}

// ── Derive plan-mix from subscriptions ───────────────────────────────────────
function buildPlanMix(subscriptions = []) {
  const counts = {};
  subscriptions.forEach((s) => {
    const plan = s.planKey ?? s.planName ?? s.tier ?? "Unknown";
    counts[plan] = (counts[plan] || 0) + 1;
  });
  return Object.entries(counts).map(([plan, count]) => ({ plan, count }));
}

// ── Derive expiring subscriptions (within 60 days) ────────────────────────────
function buildExpiring(subscriptions = []) {
  const now = Date.now();
  return subscriptions
    .map((s) => {
      const exp = s.expiresAt ?? s.endDate ?? s.renewalDate;
      const days = exp
        ? Math.ceil((new Date(exp).getTime() - now) / 86_400_000)
        : null;
      return { ...s, daysRemaining: days };
    })
    .filter(
      (s) =>
        s.daysRemaining !== null &&
        s.daysRemaining <= 60 &&
        s.daysRemaining >= 0,
    )
    .sort((a, b) => a.daysRemaining - b.daysRemaining)
    .slice(0, 10);
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bannerDismissed, setBanner] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fire all real API calls in parallel
      const [
        dashRes,
        meetingsRes,
        tenantsRes,
        subsRes,
        integrationsRes,
        jobsRes,
      ] = await Promise.allSettled([
        platformDashboardAPI(),
        getMeetingsAPI(),
        getTenantsAPI(),
        getSubscriptionsAPI(),
        getIntegrationsMonitorAPI(),
        getJobsFailuresAPI(),
      ]);

      // ── Extract values safely (allSettled never throws) ──────────────────
      const dash = dashRes.status === "fulfilled" ? dashRes.value.data : {};
      const meetings =
        meetingsRes.status === "fulfilled" ? meetingsRes.value.data : [];
      const tenants =
        tenantsRes.status === "fulfilled" ? tenantsRes.value.data : [];
      const subs = subsRes.status === "fulfilled" ? subsRes.value.data : [];
      const integrations =
        integrationsRes.status === "fulfilled"
          ? integrationsRes.value.data
          : [];
      const jobsData = jobsRes.status === "fulfilled" ? jobsRes.value.data : {};

      // Normalise arrays — backends sometimes wrap in { data: [...] }
      const meetingList = Array.isArray(meetings)
        ? meetings
        : (meetings?.data ?? []);
      const tenantList = Array.isArray(tenants)
        ? tenants
        : (tenants?.data ?? []);
      const subList = Array.isArray(subs) ? subs : (subs?.data ?? []);
      const integrationList = Array.isArray(integrations)
        ? integrations
        : (integrations?.data ?? []);

      // ── Failed jobs count ─────────────────────────────────────────────────
      const failedJobsCount =
        jobsData?.failedCount ??
        jobsData?.count ??
        (Array.isArray(jobsData?.data) ? jobsData.data.length : 0) ??
        0;

      // ── Active tenants ────────────────────────────────────────────────────
      const activeTenants = tenantList.filter(
        (t) => t.status === "active" || t.isActive === true,
      ).length;

      // ── Meetings this month ───────────────────────────────────────────────
      const now = new Date();
      const monthStart = new Date(
        now.getFullYear(),
        now.getMonth(),
        1,
      ).toISOString();
      const meetingsMonth = meetingList.filter(
        (m) => (m.startAt ?? m.createdAt ?? "") >= monthStart,
      ).length;

      // ── AI summaries — use platform dashboard value if available ──────────
      const aiSummaries =
        dash?.kpis?.aiSummaries ??
        dash?.aiSummaries ??
        dash?.stats?.aiSummaries ??
        "0";

      // ── MRR — use platform dashboard value if available ───────────────────
      const mrrUsd =
        dash?.kpis?.mrrUsd ??
        dash?.mrrUsd ??
        dash?.billing?.mrr ??
        (subList.reduce(
          (acc, s) => acc + (s.monthlyValue ?? s.amount ?? 0),
          0,
        ) ||
          "—");

      // ── Top 10 tenants by usage ───────────────────────────────────────────
      const tenantMeetingCounts = {};
      meetingList.forEach((m) => {
        const tid = m.tenantId;
        if (tid) tenantMeetingCounts[tid] = (tenantMeetingCounts[tid] || 0) + 1;
      });
      const totalMeetings = meetingList.length || 1;
      const topTenants = tenantList
        .map((t) => ({
          tenantId: t._id ?? t.id ?? t.tenantId,
          tenantCode: t.code ?? t.tenantCode ?? t.slug ?? t._id,
          companyName: t.companyName ?? t.name ?? "—",
          meetingsCount: tenantMeetingCounts[t._id ?? t.tenantId] ?? 0,
          pctOfTotal: (
            ((tenantMeetingCounts[t._id ?? t.tenantId] ?? 0) / totalMeetings) *
            100
          ).toFixed(1),
        }))
        .sort((a, b) => b.meetingsCount - a.meetingsCount)
        .slice(0, 10);

      // ── Critical alerts — derive from dashboard or empty ──────────────────
      const criticalAlerts = dash?.criticalAlerts ?? dash?.alerts ?? [];

      // ── Assemble final data shape ─────────────────────────────────────────
      setData({
        kpis: {
          totalTenants: tenantList.length,
          activeTenants,
          meetingsThisMonth: meetingsMonth,
          aiSummaries,
          failedJobs: failedJobsCount,
          mrrUsd,
        },
        meetingsByDay: buildMeetingsByDay(meetingList),
        planMixData: buildPlanMix(subList),
        connectorStrip: buildConnectorStrip(integrationList),
        topTenantsByUsage: topTenants,
        expiringSubscriptions: buildExpiring(subList),
        criticalAlerts: Array.isArray(criticalAlerts) ? criticalAlerts : [],
        lastSyncedAt: new Date().toISOString(),
        dataFreshnessMinutes: 0,
      });
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Chart configs ──────────────────────────────────────────────────────────
  const lineOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "inherit",
      },
      stroke: { curve: "smooth", width: 2.5 },
      xaxis: {
        categories: (data?.meetingsByDay || []).map((d) => d.date.slice(5)),
        labels: { rotate: -45, style: { colors: "#a390c0", fontSize: "11px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { colors: "#a390c0", fontSize: "11px" } } },
      colors: ["#644998"],
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.25,
          opacityTo: 0,
          stops: [0, 100],
        },
      },
      dataLabels: { enabled: false },
      grid: { strokeDashArray: 4, borderColor: "rgba(100,73,152,.1)" },
      tooltip: { theme: "light" },
    }),
    [data],
  );

  const lineSeries = useMemo(
    () => [
      {
        name: "Meetings",
        data: (data?.meetingsByDay || []).map((d) => d.count),
      },
    ],
    [data],
  );

  const donutOptions = useMemo(
    () => ({
      labels: (data?.planMixData || []).map((p) => p.plan),
      legend: { position: "bottom", fontSize: "12px" },
      colors: ["#644998", "#9b7dd4", "#c4a8f0", "#e0d1ff"],
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: "Total",
                fontWeight: 700,
                color: "#1e1433",
              },
            },
          },
        },
      },
      dataLabels: { enabled: false },
      stroke: { show: false },
      tooltip: { theme: "light" },
    }),
    [data],
  );

  const donutSeries = useMemo(
    () => (data?.planMixData || []).map((p) => p.count),
    [data],
  );

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Fragment>
        <PageHeader
          title="Platform Dashboard"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Dashboard" },
          ]}
        />
        <Container fluid className="mh-page-body">
          <LoadingSkeleton rows={6} cols={4} />
        </Container>
      </Fragment>
    );
  }

  if (error) {
    return (
      <Fragment>
        <PageHeader
          title="platform Dashboard"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Dashboard" },
          ]}
        />
        <Container fluid className="mh-page-body">
          <EmptyState
            icon={AlertTriangle}
            title="Could not load dashboard"
            description={error.message || "Something went wrong."}
            actionLabel="Retry"
            onAction={load}
          />
        </Container>
      </Fragment>
    );
  }

  const {
    kpis,
    connectorStrip,
    topTenantsByUsage,
    expiringSubscriptions,
    criticalAlerts,
  } = data;

  const showCriticalBanner =
    !bannerDismissed &&
    (criticalAlerts || []).some(
      (a) => String(a.severity).toUpperCase() === "CRITICAL",
    );

  return (
    <Fragment>
      <PageHeader
        title="Platform Dashboard"
        breadcrumbs={[
          { label: "Platform", path: platformUrl("dashboard") },
          { label: "Dashboard" },
        ]}
        secondaryActions={[
          {
            label: "Operations Cockpit",
            onClick: () => navigate(platformUrl("dashboard/cockpit")),
          },
          {
            label: "Alert Center",
            onClick: () => navigate(platformUrl("dashboard/alerts")),
          },
        ]}
      />

      <Container fluid className="mh-page-body">
        {/* ── Critical banner ── */}
        {showCriticalBanner && (
          <div className={styles.criticalBanner}>
            <div className={styles.bannerText}>
              <Zap size={16} />
              <span>
                <strong>Critical alerts active</strong> —{" "}
                {criticalAlerts.length} open item(s) require attention.
              </span>
            </div>
            <div className={styles.bannerActions}>
              <button
                className={styles.btnOutline}
                onClick={() => navigate(platformUrl("dashboard/alerts"))}
              >
                View alerts
              </button>
              <button
                className={styles.btnGhost}
                onClick={() => setBanner(true)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* ── KPIs ── */}
        <div className={styles.kpiGrid}>
          <KPI
            title="Total Tenants"
            value={kpis.totalTenants}
            icon={Users}
            color="#644998"
            navigate={navigate}
          />
          <KPI
            title="Active Tenants"
            value={kpis.activeTenants}
            icon={CheckCircle}
            color="#22c55e"
            navigate={navigate}
          />
          <KPI
            title="Meetings This Month"
            value={kpis.meetingsThisMonth}
            icon={Video}
            color="#06b6d4"
            navigate={navigate}
          />
          <KPI
            title="AI Summaries"
            value={kpis.aiSummaries}
            icon={Cpu}
            color="#a855f7"
            navigate={navigate}
          />
          <KPI
            title="Failed Jobs"
            value={kpis.failedJobs}
            icon={AlertCircle}
            color="#ef4444"
            to={`${platformUrl("jobs")}?status=failed`}
            navigate={navigate}
          />
          <KPI
            title="MRR"
            value={kpis.mrrUsd}
            icon={DollarSign}
            color="#22c55e"
            navigate={navigate}
          />
        </div>

        {/* ── Charts ── */}
        <div className={styles.chartRow}>
          <CardShell title="Meetings by Day">
            <div className={styles.cardBody}>
              {data.meetingsByDay?.length > 0 ? (
                <Chart
                  options={lineOptions}
                  series={lineSeries}
                  type="area"
                  height={300}
                />
              ) : (
                <p className={styles.emptyChart}>No meeting data available.</p>
              )}
            </div>
          </CardShell>
          <CardShell title="Plan Mix">
            <div className={styles.cardBody}>
              {donutSeries.length > 0 ? (
                <Chart
                  options={donutOptions}
                  series={donutSeries}
                  type="donut"
                  height={300}
                />
              ) : (
                <p className={styles.emptyChart}>
                  No subscription data available.
                </p>
              )}
            </div>
          </CardShell>
        </div>

        {/* ── Connector strip ── */}
        {connectorStrip.length > 0 && (
          <div className={styles.connectorStrip}>
            <div className={styles.connectorTitle}>Connector Health</div>
            <div className={styles.connectorPills}>
              {connectorStrip.map((c) => {
                const ok = c.status === "healthy";
                const warn = c.status === "degraded";
                const cls = ok ? "healthy" : warn ? "degraded" : "down";
                const sym = ok ? "✓" : warn ? "⚠" : "✕";
                const label = ok ? "Healthy" : warn ? "Degraded" : "Down";
                return (
                  <button
                    key={c.id}
                    className={`${styles.connectorPill} ${styles[cls]}`}
                    onClick={() =>
                      navigate(
                        `${platformUrl("integrations")}?provider=${c.providerKey || c.id}`,
                      )
                    }
                  >
                    {sym} {c.name} <span style={{ opacity: 0.7 }}>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Data tables ── */}
        <div className={styles.dataRow}>
          {/* Top 10 tenants */}
          <CardShell title="Top 10 Tenants by Usage">
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Company</th>
                  <th>Meetings</th>
                  <th>% Total</th>
                </tr>
              </thead>
              <tbody>
                {topTenantsByUsage.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        textAlign: "center",
                        padding: "1rem",
                        color: "#888",
                      }}
                    >
                      No data
                    </td>
                  </tr>
                ) : (
                  topTenantsByUsage.map((t) => {
                    const pct = parseFloat(t.pctOfTotal) || 0;
                    return (
                      <tr
                        key={t.tenantId}
                        className={styles.clickable}
                        onClick={() =>
                          navigate(platformUrl(`tenants/${t.tenantId}`))
                        }
                      >
                        <td>
                          <span className={styles.tenantCode}>
                            {t.tenantCode}
                          </span>
                        </td>
                        <td>{t.companyName}</td>
                        <td>{t.meetingsCount?.toLocaleString?.() ?? "—"}</td>
                        <td>
                          <div className={styles.pctBar}>
                            <div className={styles.bar}>
                              <div
                                className={styles.fill}
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </div>
                            <span>{t.pctOfTotal ?? "—"}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </CardShell>

          {/* Expiring subscriptions */}
          <CardShell title="Expiring Subscriptions">
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Plan</th>
                  <th>Days</th>
                </tr>
              </thead>
              <tbody>
                {expiringSubscriptions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        textAlign: "center",
                        padding: "1rem",
                        color: "#888",
                      }}
                    >
                      None expiring soon
                    </td>
                  </tr>
                ) : (
                  expiringSubscriptions.map((e) => {
                    const d = e.daysRemaining ?? 30;
                    const cls =
                      d < 14
                        ? styles.expiryUrgent
                        : d < 30
                          ? styles.expiryWarn
                          : "";
                    return (
                      <tr key={e._id ?? e.id}>
                        <td
                          title={e.companyName}
                          style={{
                            maxWidth: 110,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {e.companyName ?? e.tenantCode ?? "—"}
                        </td>
                        <td>
                          <span className={styles.planBadge}>
                            {e.plan ?? e.planName ?? "—"}
                          </span>
                        </td>
                        <td className={cls}>{d}d</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </CardShell>

          {/* Critical alerts */}
          <CardShell title="Critical Alerts">
            {criticalAlerts.length === 0 ? (
              <p
                style={{ padding: "1rem", color: "#888", fontSize: "0.875rem" }}
              >
                No critical alerts.
              </p>
            ) : (
              <ul className={styles.alertList}>
                {criticalAlerts.slice(0, 5).map((a) => {
                  const sev = String(a.severity).toLowerCase();
                  return (
                    <li key={a.id ?? a._id} className={styles.alertItem}>
                      <span
                        className={`${styles.alertBadge} ${styles[sev] || styles.critical}`}
                      >
                        {a.severity}
                      </span>
                      <div className={styles.alertTitle}>{a.title}</div>
                      <button
                        className={styles.alertLink}
                        onClick={() =>
                          navigate(platformUrl("dashboard/alerts"))
                        }
                      >
                        Open <ExternalLink size={11} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardShell>
        </div>

        {/* ── Footer ── */}
        <footer className={styles.footer}>
          <span>
            Last synced:{" "}
            {data.lastSyncedAt
              ? new Date(data.lastSyncedAt).toLocaleString()
              : "—"}
          </span>
          <span className={styles.dot} />
          <span>Data freshness: {data.dataFreshnessMinutes} min ago</span>
        </footer>
      </Container>
    </Fragment>
  );
}
